import React, { Ref, forwardRef, useCallback } from 'react';
import { Chip, TextField, useTheme } from '@mui/material';

const DELIMITER = /\n/;

export default forwardRef(function TagsInput(
    props: {
        tags: string[];
        onSelectTags: (tags: string[]) => void;
        placeholder: string;
        onFocus?: React.FocusEventHandler;
        onBlur?: React.FocusEventHandler;
        onDone?: () => unknown;
    },
    textFieldRef: Ref<HTMLInputElement>,
) {
    const [inputValue, setInputValue] = React.useState('');

    const theme = useTheme();

    const handleInput = useCallback(
        (input: string, isComplete?: boolean) => {
            const newSelectedItem = [...props.tags];
            const bits = input.split(DELIMITER).map((bit) => bit.trim());
            if (isComplete) {
                for (const bit of bits) {
                    if (
                        !bit ||
                        newSelectedItem.indexOf(bit) >= 0 // duplicate
                    )
                        continue;

                    newSelectedItem.push(bit);
                }
                if (newSelectedItem.length > props.tags.length) {
                    props.onSelectTags(newSelectedItem);
                }
            }

            setInputValue(isComplete ? '' : bits[bits.length - 1]);
        },
        [props.tags, props.onSelectTags],
    );

    return (
        <TextField
            inputRef={textFieldRef}
            InputProps={{
                placeholder: props.placeholder,
                startAdornment: props.tags.map((item) => (
                    <Chip
                        key={item}
                        tabIndex={-1}
                        label={item}
                        sx={{ p: 1 }}
                        onClick={() => {
                            navigator.clipboard.writeText(item).catch((err) => {
                                console.error('Failed to copy text: ', err);
                            });
                        }}
                        onDelete={() => {
                            const newSelectedItem = [...props.tags];
                            newSelectedItem.splice(newSelectedItem.indexOf(item), 1);
                            props.onSelectTags(newSelectedItem);
                        }}
                    />
                )),
                value: inputValue,
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    const target = event.target as HTMLInputElement;
                    if (event.key === 'Enter') {
                        event.stopPropagation();
                        event.preventDefault();
                        if (target.value) {
                            handleInput(target.value, true);
                        } else {
                            props.onDone?.();
                        }
                    }
                    if (props.tags.length && !inputValue.length && event.key === 'Backspace') {
                        props.onSelectTags(props.tags.slice(0, props.tags.length - 1));
                    }
                },
                onFocus: props.onFocus,
                onBlur: props.onBlur,
                onPaste: (event) => {
                    handleInput(event.clipboardData.getData('text'), true);
                    event.preventDefault();
                    event.stopPropagation();
                },
                onChange: (event) => {
                    handleInput(event.target.value, false);
                },
                sx: {
                    flexWrap: 'wrap',
                    p: 1,
                    gap: theme.spacing(1),
                    '& input': {
                        p: theme.spacing(0.5),
                        width: 'auto',
                        flex: 1,
                        minWidth: 'fit-content',
                    },
                },
            }}
        />
    );
});
